/**
 * Text.js — v2
 *
 * Cambio: nueva prop `className` que se aplica al elemento DOM renderizado.
 *
 * Casos clave que esto resuelve:
 *  - <span class="material-icons">visibility</span>
 *    → Text con className="material-icons"
 *    → Renderiza <span className="material-icons" style={...}>visibility</span>
 *    → El font Material Icons (cargado en el app principal via index.html)
 *      convierte "visibility" en el ícono de ojo ✓
 *
 *  - <span class="card-channeled-badge">Jeshua</span>
 *    → Text con className="card-channeled-badge"
 *    → CSS del StylesBlock aplica el fondo naranja y border-radius ✓
 */
import React from 'react';
import { useNode } from '@craftjs/core';
import { TextField, FormControl, FormLabel, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  settings: { padding: theme.spacing(2) },
  rawStyleTextarea: {
    width: '100%',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.68rem',
    padding: theme.spacing(1),
    border: '1px solid #ccc',
    borderRadius: 4,
    minHeight: 80,
    resize: 'vertical',
    outline: 'none',
    lineHeight: 1.5,
    color: '#333',
    backgroundColor: '#fafafa',
  },
  hint: { fontSize: '0.72rem', color: '#999', marginTop: 4, marginBottom: theme.spacing(1) },
}));

const VALID_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'label', 'li', 'div'];

export const Text = ({ text, fontSize, fontWeight, textAlign, color, tagName, className, rawStyle }) => {
  const { connectors: { connect, drag } } = useNode();

  const style = {
    ...rawStyle,
    fontSize:   fontSize   ? `${fontSize}px`  : rawStyle?.fontSize,
    fontWeight: fontWeight || rawStyle?.fontWeight,
    textAlign:  textAlign  || rawStyle?.textAlign,
    color:      color      || rawStyle?.color,
    cursor:     'pointer',
  };

  const Tag = VALID_TAGS.includes(tagName) ? tagName : 'div';

  return (
    <Tag
      ref={(ref) => connect(drag(ref))}
      className={className || undefined}  // ← aplica clase original
      style={style}
      dangerouslySetInnerHTML={
        text && text.includes('<') ? { __html: text } : undefined
      }
    >
      {(!text || !text.includes('<')) ? text : undefined}
    </Tag>
  );
};

const TextSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    text, fontSize, fontWeight, textAlign, color, tagName, className, rawStyle,
  } = useNode((node) => ({
    text:       node.data.props.text,
    fontSize:   node.data.props.fontSize,
    fontWeight: node.data.props.fontWeight,
    textAlign:  node.data.props.textAlign,
    color:      node.data.props.color,
    tagName:    node.data.props.tagName,
    className:  node.data.props.className,
    rawStyle:   node.data.props.rawStyle,
  }));

  const rawStyleStr = rawStyle ? JSON.stringify(rawStyle, null, 2) : '{}';

  return (
    <div className={classes.settings}>

      {/* Clase CSS original */}
      {className && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Clase CSS original</FormLabel>
          <TextField
            size="small"
            value={className}
            onChange={(e) => setProp((props) => (props.className = e.target.value))}
            inputProps={{ style: { fontSize: '0.78rem', fontFamily: 'monospace' } }}
          />
        </FormControl>
      )}

      {/* Etiqueta */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Etiqueta HTML</FormLabel>
        <TextField select value={tagName || 'p'}
          onChange={(e) => setProp((props) => (props.tagName = e.target.value))}
          SelectProps={{ native: true }}
        >
          <option value="h1">h1 — Título principal</option>
          <option value="h2">h2 — Título secundario</option>
          <option value="h3">h3</option>
          <option value="h4">h4</option>
          <option value="h5">h5</option>
          <option value="h6">h6</option>
          <option value="p">p — Párrafo</option>
          <option value="span">span — Inline</option>
          <option value="label">label</option>
          <option value="li">li — Lista</option>
          <option value="div">div — Genérico</option>
        </TextField>
      </FormControl>

      {/* Texto */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Texto</FormLabel>
        <TextField
          value={text || ''}
          onChange={(e) => setProp((props) => (props.text = e.target.value))}
          multiline rows={3}
        />
      </FormControl>

      {/* Tamaño */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Tamaño de fuente (px)</FormLabel>
        <TextField type="number" value={fontSize || 16}
          onChange={(e) => setProp((props) => (props.fontSize = e.target.value))} />
      </FormControl>

      {/* Peso */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Peso de fuente</FormLabel>
        <TextField select value={fontWeight || '400'}
          onChange={(e) => setProp((props) => (props.fontWeight = e.target.value))}
          SelectProps={{ native: true }}
        >
          <option value="300">300 — Light</option>
          <option value="400">400 — Normal</option>
          <option value="500">500 — Medium</option>
          <option value="600">600 — Semibold</option>
          <option value="700">700 — Bold</option>
          <option value="900">900 — Black</option>
        </TextField>
      </FormControl>

      {/* Alineación */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Alineación</FormLabel>
        <TextField select value={textAlign || 'left'}
          onChange={(e) => setProp((props) => (props.textAlign = e.target.value))}
          SelectProps={{ native: true }}
        >
          <option value="left">Izquierda</option>
          <option value="center">Centro</option>
          <option value="right">Derecha</option>
          <option value="justify">Justificado</option>
        </TextField>
      </FormControl>

      {/* Color */}
      <FormControl fullWidth margin="normal">
        <FormLabel>Color</FormLabel>
        <TextField type="color" value={color || '#000000'}
          onChange={(e) => setProp((props) => (props.color = e.target.value))} />
      </FormControl>

      {/* rawStyle */}
      {rawStyle && Object.keys(rawStyle).length > 0 && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Estilos heredados (CSS)</FormLabel>
          <Typography className={classes.hint}>Las props de arriba tienen prioridad.</Typography>
          <textarea
            className={classes.rawStyleTextarea}
            defaultValue={rawStyleStr}
            onBlur={(e) => {
              try { setProp((props) => (props.rawStyle = JSON.parse(e.target.value))); }
              catch (_) {}
            }}
            spellCheck={false}
            style={{ marginTop: 8 }}
          />
        </FormControl>
      )}
    </div>
  );
};

Text.craft = {
  props: {
    text:       'Escribe tu texto aquí',
    tagName:    'p',
    className:  '',
    fontSize:   16,
    fontWeight: '400',
    textAlign:  'left',
    color:      '#000000',
    rawStyle:   {},
  },
  related: { settings: TextSettings },
};